import {
  Business,
  RootStateBusiness,
  alert,
  Member,
  EBillingStatusTypes
} from './businessTypes';
import { ActionTree, MutationTree, GetterTree } from 'vuex';
import { RootState } from '../types';
import Api, { setAppCookie } from '@/api/index';
import BusinessService from '@/api/BusinessService';
import ManagerService from '@/api/ManagerService';
import BillingService from '@/api/BillingService';
import eventBus from '@/utils/eventBus';
import Module from '../interfaces/module.interface';
import { IRouterProps } from '@/router/interfaces/router-props.interface';
import flagKeys from '@/constants/flagKeys';
import { isRemoteLoading } from '@/utils/remote-loading';
import { IBusinessType } from '@/models/business';

const businessService = new BusinessService();
const managerService = new ManagerService();
const billingService = new BillingService();

export const state: RootStateBusiness = new RootStateBusiness();

const factory = async (context: IRouterProps) => {
  if (
    context.auth === 'manager' &&
    context.routeRequirements.managerHaveBusiness
  ) {
    const { data } = await businessService.getBusinessInfo();

    if (
      context.store.getters['flags/getFlags'].includes(
        flagKeys.billingModuleV2
      ) &&
      data.subscriptionStatus !== EBillingStatusTypes.UNAVAILABLE
    ) {
      billingService
        .getBillingOverviewSubscription()
        .then(({ data: subscription }) => {
          context.store.dispatch(
            'business/SET_BILLING_SUBSCRIPTION',
            subscription
          );
        });
    }

    data.manager = data.loggedInManager;

    if (
      data.type === IBusinessType.INSTORE &&
      context.store.getters['flags/getFlags'].includes(
        flagKeys.newMerchantPortal
      ) &&
      window.self === window.top
    ) {
      setAppCookie('instore');
      const currentPath = window.location.pathname.replace('/dashboard', '');
      const currentSearch = window.location.search;

      if (currentPath === '/' || currentPath === '/home') {
        location.href = `/?${+Date.now()}`;
      } else {
        const separator = currentSearch ? '&' : '?';
        location.href = `${currentPath}${currentSearch}${separator}time=${+Date.now()}`;
      }

      return;
    }

    eventBus.$emit('setBusinessInfo', data);
    context.store.dispatch('business/SET_BUSINESS_INFO', data);
    setTimeout(
      () => context.store.dispatch('business/GET_TEAM_MEMBERS', data),
      100
    );
    businessService.getBusinessAdditionalInfo().then(({ data }) => {
      context.store.dispatch('business/SET_BUSINESS_ADDITIONAL_INFO', data);
    });
  }
  if (
    context.auth === 'manager' &&
    context.store.getters['business/getBusinessInfo'].business
  ) {
    setTimeout(() => {
      businessService.getRewardStatusList().then(({ data: rewardData }) => {
        context.store.dispatch('business/SET_REWARD_STATUS', rewardData);
      });
    }, 100);
  }
};

const actions: ActionTree<RootStateBusiness, RootState> = {
  SET_BUSINESS_INFO(context, payload: Business) {
    context.commit('setBusinessInfo', payload);
  },
  SET_BUSINESS_ADDITIONAL_INFO(context, payload: Business) {
    context.commit('setBusinessAdditionalInfo', payload);
  },
  SET_BILLING_SUBSCRIPTION(context, payload: Business) {
    context.commit('setBillingSubscription', payload);
  },
  UPDATE_BUSINESS_PHONE(context, payload: string) {
    context.commit('updateBusinessPhone', payload);
  },
  SET_BUSINESS_PICTURE(context, payload: any) {
    context.commit('setBusinessPicture', payload);
  },
  PUSH_ALERT(context, payload: alert) {
    context.commit('pushAlert', payload);
  },
  PUT_ALERT(context, payload: alert) {
    context.commit('putAlert', payload);
  },
  SET_BUSINESS_DETAIL_FORM(context, payload: Business) {
    context.commit('setBusinessDetailForm', payload);
  },
  SET_WIDGET_WARN_CHANGES(context, payload: any) {
    context.commit('setWidgetWarnChanges', payload);
  },
  SET_REWARD_STATUS(context, payload: any) {
    context.commit('setRewardStatus', payload);
  },
  RESET_BUSINESS_DETAIL_FORM(context) {
    context.commit('resetBusinessDetailForm');
  },
  GET_TEAM_MEMBERS({ commit }) {
    managerService.getTeamMembers().then(response => {
      const members: Member[] = [];
      response.data.items.map((item: any) => {
        const member = {
          id: item.manager.id,
          fullname: item.manager.fullname,
          status: item.managerInvite.status,
          mail: item.managerInvite.mail,
          businessId: item.business.id,
          managerId: item.manager.id,
          inviteId: item.managerInvite.id
        };

        members.push(member);
      });
      commit('setBusinessTeamMembers', members);
    });
  }
};

const mutations: MutationTree<RootStateBusiness> = {
  setBusinessInfo(state, payload: Business) {
    if (isRemoteLoading()) {
      window.parent.postMessage(
        {
          type: 'set-business-info',
          data: { ...payload }
        },
        '*'
      );
    }
    state.business = payload;
  },
  setBusinessAdditionalInfo(state, payload: Business) {
    state.business = {
      ...state.business,
      ...payload
    };
  },
  setBillingSubscription(state, payload: any) {
    if (isRemoteLoading()) {
      window.parent.postMessage(
        {
          type: 'set-billing-subscription',
          data: { ...payload }
        },
        '*'
      );
    }
    state.subscription = payload;
  },
  updateBusinessPhone(state, payload: any) {
    if (state.business) {
      state.business.phone = payload.number;
      state.business.phoneCountryCode = payload.code;
      state.business.phoneCountryIso = payload.iso;
    }
  },
  setBusinessDetailForm(state, payload: Business) {
    state.businessDetailForm = payload;
  },
  setWidgetWarnChanges(state, payload: any) {
    state.widgetWarnChanges = payload;
  },
  setRewardStatus(state, payload: any) {
    state.rewardStatus = payload;
  },
  setBusinessPicture(state, payload: any) {
    if (state.business) {
      state.business.logo = payload;
    }
  },
  setBusinessTeamMembers(state, payload: Member[]) {
    state.business = {
      ...(state.business || {}),
      teamMembers: payload
    };
  },
  putAlert(state, payload: alert) {
    if (state.alerts) {
      const index = state.alerts.findIndex(
        alert => alert.type === payload.type
      );
      state.alerts[index].active = payload.active;
    }
  },
  pushAlert(state, payload: alert) {
    if (state.alerts) {
      state.alerts.push(payload);
    } else {
      state.alerts = [payload];
    }
  },
  resetBusinessDetailForm(state) {
    state.businessDetailForm = new Business();
  }
};

const getters: GetterTree<RootStateBusiness, RootState> = {
  getManagerRole(state) {
    return state.business && state.business.manager
      ? state.business.manager.role
      : null;
  },
  getBusinessState(state) {
    return state.business;
  },
  getWidgetWarnChanges(state) {
    return state.widgetWarnChanges;
  },
  getRewardStatus(state) {
    return state.rewardStatus;
  },
  getBillingSubscription(state) {
    return state.subscription;
  },
  getBusinessInfo(state) {
    return state;
  },
  getBusinessDetailForm(state) {
    return state.businessDetailForm;
  },
  getBusinessType(state) {
    return state.business ? state.business.type : null;
  },
  getAlerts(state) {
    return state.alerts;
  },
  getAlertsType(state) {
    return state.alerts ? state.alerts.map(alert => alert.type) : [];
  },
  getActiveAlertsType(state) {
    return state.alerts
      ? state.alerts.filter(alert => alert.active).map(alert => alert.type)
      : [];
  },
  isRootView() {
    return window.location.pathname.includes('/admin');
  },
  isRootUser() {
    return localStorage.getItem(`${window.location.host}-${Api.SUPER_ADMIN}`);
  }
};

const namespaced: boolean = true;

export const business: Module<RootStateBusiness, RootState> = {
  namespaced,
  initializer: {
    factory,
    on: 'BeforeEachRoute',
    provide: 'BusinessStore'
  },
  state,
  actions,
  mutations,
  getters
};
